import { useState, useEffect } from 'react';
import { Target, Heart, Activity, Zap, Moon, Apple, TrendingDown, TrendingUp, Dumbbell, Brain, Shield, RefreshCw, ArrowLeft, ArrowRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

// Mapare inversă: din enum (backend) → id frontend
const ENUM_TO_FRONTEND: { [key: string]: string } = {
  'WEIGHT_LOSS': 'lose_weight',
  'MUSCLE_GAIN': 'gain_muscle',
  'HEALTHY_LIFESTYLE': 'healthy_lifestyle',
  'REDUCE_BELLY_FAT': 'reduce_belly_fat',
  'INCREASE_ENERGY': 'more_energy',
  'CLEAN_EATING': 'eat_clean',
  'REDUCE_SUGAR_CARBS': 'reduce_sugar',
  'BETTER_SLEEP': 'sleep_better',
  'REDUCE_BLOATING': 'reduce_bloating',
  'START_EXERCISE': 'start_exercise',
  'IMPROVE_FOCUS': 'mental_focus',
  'MAINTAIN_WEIGHT': 'boost_immunity',
  'HEALTHY_WEIGHT_GAIN': 'healthy_weight_gain',
  // CHRONIC MODE
  'EASY_STANDING': 'stand_from_chair',
  'REDUCE_INFLAMMATION': 'reduce_inflammation',
  'RECOVER_MUSCLE_STRENGTH': 'recover_strength',
  'CLIMB_STAIRS': 'climb_stairs',
  'REDUCE_JOINT_PAIN': 'reduce_joint_pain',
  'BREATHE_BETTER': 'breathe_better',
  'STABILIZE_GLUCOSE': 'stabilize_glucose',
  'IMPROVE_BALANCE': 'improve_balance',
  'REDUCE_CHRONIC_FATIGUE': 'reduce_fatigue',
  'IMPROVE_DIGESTION': 'improve_digestion',
  'REGAIN_MOBILITY': 'regain_mobility',
  'PREVENT_DISEASE_WORSENING': 'prevent_worsening',
  'HEALTHY_WEIGHT_LOSS_CHRONIC': 'lose_weight_safe',
  'CONTROL_CHRONIC_PAIN': 'manage_pain',
  'IMPROVE_LIFE_WITH_DISEASE': 'improve_life_quality',
};

// HEALTHY MODE GOALS (13 obiective)
const HEALTHY_GOALS = [
  { id: 'lose_weight', icon: TrendingDown, title: 'Vreau să slăbesc', desc: 'Plan hipocaloric, exerciții cardio, tracking calorii' },
  { id: 'gain_muscle', icon: Dumbbell, title: 'Vreau masă musculară', desc: 'Plan hiperproteic, antrenamente forță, suplimente' },
  { id: 'healthy_weight_gain', icon: TrendingUp, title: 'Creștere în greutate sănătoasă', desc: 'Plan caloric echilibrat, nutriție densă, masă musculară' },
  { id: 'healthy_lifestyle', icon: Heart, title: 'Stil de viață sănătos', desc: 'Nutriție echilibrată, mișcare zilnică, somn calitativ' },
  { id: 'reduce_belly_fat', icon: Activity, title: 'Reduc grăsimea abdominală', desc: 'HIIT, post intermitent, măsurători periodice' },
  { id: 'more_energy', icon: Zap, title: 'Mai multă energie zilnic', desc: 'Dietă antiinflamatoare, evitare food crash-uri' },
  { id: 'eat_clean', icon: Apple, title: 'Mănânc curat și natural', desc: 'Fără procesate, alimente curate, analiză etichete' },
  { id: 'reduce_sugar', icon: RefreshCw, title: 'Reduc zahărul/carbohidrații', desc: 'Rețete low-carb, educație nutrițională' },
  { id: 'sleep_better', icon: Moon, title: 'Dorm mai bine', desc: 'Rutine seară, alimente pentru somn, detox digital' },
  { id: 'reduce_bloating', icon: Activity, title: 'Scap de balonare', desc: 'Dietă FODMAP, monitorizare reacții alimente' },
  { id: 'start_exercise', icon: Dumbbell, title: 'Încep sport ușor', desc: 'Video pas cu pas, nivel începător, pași zilnici' },
  { id: 'mental_focus', icon: Brain, title: 'Îmbunătățesc concentrarea', desc: 'Dietă pentru creier (omega 3, magneziu)' },
  { id: 'boost_immunity', icon: Shield, title: 'Întăresc imunitatea', desc: 'Vitamine, alimente antiinflamatorii' }
];

// CHRONIC MODE GOALS (15 obiective funcționale)
const CHRONIC_GOALS = [
  { id: 'stand_from_chair', icon: Activity, title: 'Mă ridic ușor de pe scaun', desc: 'Exerciții forță picioare, progres monitorizat' },
  { id: 'reduce_inflammation', icon: Heart, title: 'Reduc inflamația', desc: 'Plan antiinflamator, eliminare alimente pro-inflamatorii' },
  { id: 'recover_strength', icon: Dumbbell, title: 'Recuperez forța musculară', desc: 'Exerciții cu greutate proprie, kinetoterapie' },
  { id: 'climb_stairs', icon: TrendingDown, title: 'Urc scările fără durere', desc: 'Respirație, mobilitate articulară, exerciții practice' },
  { id: 'reduce_joint_pain', icon: Activity, title: 'Reduc durerile articulare', desc: 'Nutriție antiinflamatoare + exerciții blânde' },
  { id: 'breathe_better', icon: Zap, title: 'Respir mai bine', desc: 'Exerciții respirație, poziții somn, plan anti-mucus' },
  { id: 'stabilize_glucose', icon: Apple, title: 'Stabilizez glicemia', desc: 'Alimentație pentru diabet, grafic evoluție, rețete sigure' },
  { id: 'improve_balance', icon: Activity, title: 'Îmbunătățesc echilibrul', desc: 'Exerciții echilibru, coordonare, prevenție căderi' },
  { id: 'reduce_fatigue', icon: Moon, title: 'Reduc oboseala cronică', desc: 'Somn profund, nutriție anti-oboseală, relaxare' },
  { id: 'improve_digestion', icon: Apple, title: 'Îmbunătățesc digestia', desc: 'Fără iritanți, FODMAP, monitorizare simptome' },
  { id: 'regain_mobility', icon: RefreshCw, title: 'Recapăt mobilitatea', desc: 'Exerciții zilnice adaptate, urmărire progres' },
  { id: 'prevent_worsening', icon: Shield, title: 'Previn agravarea bolii', desc: 'Rutină generală: nutriție, hidratare, mișcare' },
  { id: 'lose_weight_safe', icon: TrendingDown, title: 'Slăbesc sănătos', desc: 'Dietă ușoară, fără foame, adaptată bolii' },
  { id: 'manage_pain', icon: Heart, title: 'Controlez durerile cronice', desc: 'Jurnal durere, activități care reduc durerea' },
  { id: 'improve_life_quality', icon: Brain, title: 'Îmbunătățesc calitatea vieții', desc: 'Personalizare totală AI + protocoale + tracking' }
];

interface HealthGoalSelectorProps {
  isModal?: boolean;
  onClose?: () => void;
  onSaved?: () => void;
}

export default function HealthGoalSelector({ isModal = false, onClose, onSaved }: HealthGoalSelectorProps) {
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [selectedGoals, setSelectedGoals] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');

  // Get user from localStorage
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isHealthyMode = user.lifestyleMode === 'HEALTHY';
  
  // Select appropriate goals based on lifestyle mode
  const goals = isHealthyMode ? HEALTHY_GOALS : CHRONIC_GOALS;

  // Pre-populare cu obiectivele existente ale utilizatorului
  useEffect(() => {
    if (user.healthGoals && Array.isArray(user.healthGoals) && user.healthGoals.length > 0) {
      // Convertim din format enum (backend) în format frontend (id-uri)
      const frontendGoals = user.healthGoals
        .map((enumGoal: string) => ENUM_TO_FRONTEND[enumGoal] || null)
        .filter(Boolean) as string[];
      
      if (frontendGoals.length > 0) {
        setSelectedGoals(frontendGoals);
      }
    }
  }, []);

  const handleGoalToggle = (goalId: string) => {
    setSelectedGoals(prev => {
      if (prev.includes(goalId)) {
        return prev.filter(id => id !== goalId);
      } else {
        return [...prev, goalId];
      }
    });
  };

  const handleContinue = async () => {
    if (selectedGoals.length === 0) {
      setError('Te rog selectează cel puțin un obiectiv pentru a continua.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('accessToken');
      
      const response = await axios.put(
        `${API_URL}/api/auth/profile`,
        { 
          healthGoals: selectedGoals,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Update user in localStorage
      if (response.data.user) {
        localStorage.setItem('user', JSON.stringify(response.data.user));
      }

      // Refresh user in AuthContext so ProfileView/SettingsPage see updated goals immediately
      refreshUser();

      // Dacă e modal (din profil/setări), închidem și notificăm
      if (isModal && onSaved) {
        onSaved();
        return;
      }

      // Get profile to check completion percent
      const profileResponse = await axios.get(
        `${API_URL}/api/profile`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const completionPercent = profileResponse.data.completionPercent || 0;

      // Redirect based on profile completion
      if (completionPercent === 100) {
        navigate('/dashboard');
      } else {
        navigate('/profile/edit');
      }
    } catch (error: any) {
      console.error('Error saving health goal:', error);
      
      if (error.response?.data?.error) {
        setError(error.response.data.error);
      } else {
        setError('Eroare la salvare. Te rog încearcă din nou.');
      }
      
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (isModal && onClose) {
      onClose();
    } else {
      navigate('/dashboard');
    }
  };

  if (!user.lifestyleMode && !isModal) {
    window.location.href = '/lifestyle-mode';
    return null;
  }

  const content = (
    <div className={isModal ? '' : 'min-h-screen bg-gradient-to-br from-blue-50 via-white to-green-50 py-12 px-4'}>
      <div className={isModal ? '' : 'max-w-6xl mx-auto'}>
        {/* Back Button */}
        <button
          onClick={handleBack}
          className="mb-6 flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium">{isModal ? 'Anulează' : 'Înapoi la Dashboard'}</span>
        </button>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0">
                <svg className="w-5 h-5 text-red-600 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-red-800">{error}</p>
              </div>
              <button
                onClick={() => setError('')}
                className="flex-shrink-0 text-red-400 hover:text-red-600"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
            <Target className="w-8 h-8 text-blue-600" />
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-3">
            {isHealthyMode ? '🎯 Care sunt obiectivele tale?' : '💪 Ce vrei să îmbunătățești?'}
          </h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto mb-2">
            {isHealthyMode 
              ? 'Selectează obiectivele pe care vrei să le atingi pentru a primi un plan personalizat de nutriție și exerciții'
              : 'Selectează obiectivul funcțional pe care vrei să îl atingi pentru a primi un plan adaptat condiției tale'
            }
          </p>
          <p className="text-sm text-blue-600 font-medium">
            ✨ Poți selecta mai multe obiective și le poți schimba oricând
          </p>
        </div>

        {/* Goals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
          {goals.map((goal) => {
            const Icon = goal.icon;
            const isSelected = selectedGoals.includes(goal.id);

            return (
              <button
                key={goal.id}
                onClick={() => handleGoalToggle(goal.id)}
                disabled={loading}
                className={`relative bg-white rounded-xl p-6 border-2 transition-all hover:shadow-lg disabled:opacity-50 text-left ${
                  isSelected
                    ? 'border-blue-500 shadow-xl scale-[1.02] bg-blue-50'
                    : 'border-gray-200 hover:border-blue-300'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center transition-colors ${
                    isSelected ? 'bg-blue-500' : 'bg-gray-100'
                  }`}>
                    <Icon className={`w-6 h-6 ${isSelected ? 'text-white' : 'text-gray-600'}`} />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-semibold text-gray-900 mb-1">
                      {goal.title}
                    </h3>
                    <p className="text-sm text-gray-600 line-clamp-2">
                      {goal.desc}
                    </p>
                  </div>
                </div>

                {isSelected && (
                  <div className="absolute top-3 right-3">
                    <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                )}
              </button>
            );
          })}
        </div>

        {/* Selected Goals Summary */}
        {selectedGoals.length > 0 && (
          <div className="mb-6 bg-white border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Ai selectat {selectedGoals.length} {selectedGoals.length === 1 ? 'obiectiv' : 'obiective'}:</strong>
            </p>
            <div className="flex flex-wrap gap-2">
              {selectedGoals.map(goalId => {
                const goal = goals.find(g => g.id === goalId);
                return (
                  <span key={goalId} className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm">
                    {goal?.title}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleGoalToggle(goalId);
                      }}
                      className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                    >
                      <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* Continue Button */}
        <div className="flex justify-center mb-8">
          <button
            onClick={handleContinue}
            disabled={selectedGoals.length === 0 || loading}
            className={`
              flex items-center gap-3 px-8 py-4 rounded-xl font-semibold text-lg transition-all
              ${selectedGoals.length > 0 && !loading
                ? 'bg-blue-600 text-white hover:bg-blue-700 shadow-lg hover:shadow-xl transform hover:scale-105'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
              }
            `}
          >
            {loading ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                <span>Se salvează...</span>
              </>
            ) : (
              <>
                <span>{isModal ? 'Salvează obiectivele' : 'Continuă'}</span>
                <ArrowRight className="w-5 h-5" />
              </>
            )}
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-gradient-to-r from-blue-50 to-green-50 border border-blue-200 rounded-xl p-6 text-center">
          <p className="text-sm text-gray-700">
            💡 <strong>Flexibil!</strong> Obiectivele pot fi schimbate oricând din Profil sau Setări. 
            Vom personaliza toate recomandările AI în funcție de alegerile tale.
          </p>
        </div>

        {/* Loading Overlay */}
        {loading && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-xl p-8 text-center max-w-sm">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-700 font-medium mb-2">Se salvează obiectivele...</p>
              <p className="text-sm text-gray-500">Personalizăm experiența pentru tine</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return content;
}
